/**
 * ReservationDashboard - Vendor Orders Style Theme
 *
 * Clean white cards, amber/orange accents, consistent with VendorOrders page
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  EventSeat as SeatIcon,
  CheckCircle as ConfirmedIcon,
  Warning as WarningIcon,
  TableRestaurant as TableIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { InputAdornment, TextField as MuiTextField } from '@mui/material';
import { AppIconButton, PageHeroBand, SecondaryButton } from '../components/ui';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ReservationProvider, useReservations } from '../context/ReservationContext';
import {
  ReservationCalendar,
  ReservationModal,
  ReservationDetailModal,
  WaitlistPanel,
  TableMergePanel,
  ReservationTimeline,
} from '../components/reservations';
import { availabilityApi } from '../api/reservationApi';
import type { Reservation, ReservableTable, WaitlistEntry } from '../types/reservation.types';
import LoadingScreen from '../components/LoadingScreen';
import toast from 'react-hot-toast';

// ============================================================================
// CUSTOM STYLES (matching VendorOrders)
// ============================================================================

const getStyles = (theme: any) => `
  .reservation-dashboard {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: ${theme.background || '#F8FAFC'};
    min-height: 100vh;
    padding: 1rem;
  }

  @media (min-width: 640px) {
    .reservation-dashboard {
      padding: 1.5rem;
    }
  }

  .stat-card {
    background: ${theme.components?.card?.background || 'white'};
    border: 1px solid ${theme.components?.card?.border || '#E2E8F0'};
    border-radius: 12px;
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    transition: all 0.2s ease;
    overflow: hidden;
  }

  .stat-card:hover {
    box-shadow: ${theme.components?.card?.hoverShadow || '0 4px 12px rgba(0, 0, 0, 0.05)'};
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  @media (min-width: 768px) {
    .stat-icon {
      width: 48px;
      height: 48px;
    }
    .stat-card {
      padding: 1rem 1.25rem;
      gap: 1rem;
    }
  }

  .tab-button {
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.8125rem;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    background: transparent;
    color: ${theme.gray[500] || '#64748B'};
    white-space: nowrap;
    flex-shrink: 0;
  }

  @media (min-width: 640px) {
    .tab-button {
      padding: 0.625rem 1rem;
      font-size: 0.875rem;
      gap: 0.5rem;
    }
  }

  .tab-button:hover {
    background: ${theme.components?.card?.hover || '#F1F5F9'};
  }

  /* Tokens, not the legacy preset — the active tab is an action colour and
   * must not follow a stale themeName out of the design system. */
  .tab-button.active {
    background: var(--action);
    color: var(--action-text);
    border-color: var(--action);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .feature-chip {
    padding: 0.25rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
  }

  @media (min-width: 640px) {
    .feature-chip {
      padding: 0.375rem 0.75rem;
      font-size: 0.75rem;
      gap: 0.375rem;
    }
  }

  .feature-chip.enabled {
    background: var(--status-ok-bg);
    color: var(--status-ok-text);
    border: 1px solid var(--status-ok-border);
  }

  .feature-chip.disabled {
    background: ${theme.gray[100] || '#F1F5F9'};
    color: ${theme.gray[500] || '#64748B'};
    border: 1px solid ${theme.components?.card?.border || '#E2E8F0'};
  }

  .content-card {
    background: ${theme.components?.card?.background || 'white'};
    border: 1px solid ${theme.components?.card?.border || '#E2E8F0'};
    border-radius: 12px;
    overflow: hidden;
  }

  .primary-button {
    background: var(--action);
    color: var(--action-text);
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }

  .primary-button:hover {
    background: var(--action-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  .secondary-button {
    background: ${theme.components?.card?.background || 'white'};
    color: ${theme.text || '#475569'};
    border: 1px solid ${theme.components?.card?.border || '#E2E8F0'};
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .secondary-button:hover {
    background: ${theme.components?.card?.hover || '#F8FAFC'};
    border-color: ${theme.gray[300] || '#CBD5E1'};
  }
`;

// ============================================================================
// STATS CARD
// ============================================================================

interface StatsCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  bgColor,
}) => {
  const { theme } = useTheme();
  return (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Box sx={{ color, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{icon}</Box>
      </div>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap variant="body2" sx={{ color: theme.gray[500] || '#64748B', fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.text || '#1E293B', fontSize: { xs: '1.125rem', sm: '1.5rem' } }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography noWrap variant="caption" sx={{ color: theme.gray[300] || '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </div>
  );
};

// ============================================================================
// DASHBOARD CONTENT
// ============================================================================

const DashboardContent: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    reservations,
    dailyStats,
    waitlistStats,
    tableGroups,
    settings,
    selectedDate,
    loading,
    error,
    isReservationEnabled,
    isWaitlistEnabled,
    isTableMergeEnabled,
    notifyWaitlistCustomer,
    seatWaitlistCustomer,
  } = useReservations();

  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [viewingReservation, setViewingReservation] = useState<Reservation | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [preselectedDate, setPreselectedDate] = useState<string | undefined>();
  const [preselectedTime, setPreselectedTime] = useState<string | undefined>();
  const [availableTables, setAvailableTables] = useState<ReservableTable[]>([]);
  const [waitlistTableSelectEntry, setWaitlistTableSelectEntry] = useState<{
    entry: WaitlistEntry;
    action: 'notify' | 'seat';
  } | null>(null);

  // Load available tables
  useEffect(() => {
    const loadTables = async () => {
      try {
        const tables = await availabilityApi.getTables(
          selectedDate,
          '00:00',
          '23:59'
        );
        setAvailableTables(tables);
      } catch (err) {
        console.error('Error loading tables:', err);
      }
    };

    loadTables();
  }, [selectedDate]);

  const handleCreateReservation = (date?: string, time?: string) => {
    // Navigate to floor plan view for reservation creation (like transfer mode)
    const params = new URLSearchParams();
    params.set('reservationMode', 'true');
    if (date) params.set('date', date);
    if (time) params.set('time', time);
    navigate(`/vendor/tables?${params.toString()}`);
  };

  const handleViewReservation = (reservation: Reservation) => {
    setViewingReservation(reservation);
    setDetailModalOpen(true);
  };

  const handleEditReservation = (reservation: Reservation) => {
    // Reuse the live floor plan with a banner instead of a separate modal/picker.
    navigate(`/vendor/tables?editReservation=${reservation.id}`);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingReservation(null);
    setPreselectedDate(undefined);
    setPreselectedTime(undefined);
  };

  const handleWaitlistTableSelect = (_entry: WaitlistEntry, _action: 'notify' | 'seat') => {
    // No-op: WaitlistPanel now navigates directly to /vendor/tables?waitlistSeat=...
    // This prop is kept for backwards compatibility with older call sites.
  };

  const handleWaitlistTableSelected = async (tableId: number, tableGroupId?: number) => {
    if (!waitlistTableSelectEntry) return;

    const { entry, action } = waitlistTableSelectEntry;

    try {
      if (action === 'notify') {
        // Notify customer with selected table/group
        await notifyWaitlistCustomer(entry.id, tableId || undefined, tableGroupId);
        toast.success(`${entry.customerName} has been notified`);
      } else {
        // For seat action, we need to first notify (which assigns table), then seat
        await notifyWaitlistCustomer(entry.id, tableId || undefined, tableGroupId);
        await seatWaitlistCustomer(entry.id);
        toast.success(`${entry.customerName} has been seated`);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Operation failed';
      toast.error(message);
    } finally {
      setWaitlistTableSelectEntry(null);
    }
  };

  const handleWaitlistCancel = () => {
    setWaitlistTableSelectEntry(null);
  };

  if (loading) {
    return <LoadingScreen message="Loading reservations..." />;
  }

  if (!isReservationEnabled && !isWaitlistEnabled && !isTableMergeEnabled) {
    return (
      <div className="reservation-dashboard">
        <style>{getStyles(theme)}</style>
        <div className="content-card p-6">
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Reservation Features Disabled
            </Typography>
            <Typography variant="body2">
              All reservation features are currently disabled for your account.
              Please enable them in settings to use this page.
            </Typography>
          </Alert>
          <button className="primary-button" onClick={() => navigate('/vendor/settings')}>
            <SettingsIcon sx={{ fontSize: 18 }} />
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reservation-dashboard">
      <style>{getStyles(theme)}</style>

      {/* Header — confident-shell hero band, same as the Finance module */}
      <PageHeroBand
        eyebrow="Front of house · Reservations"
        title="Reservations"
        heroLabel="Today"
        heroValue={dailyStats ? String(dailyStats.total) : '—'}
        actions={<Box sx={{ display: 'flex', gap: 1, flexShrink: 0,
          '& .MuiButton-root': { backgroundColor: 'color-mix(in srgb, var(--band-text) 10%, transparent)', color: 'var(--band-text)', borderColor: 'color-mix(in srgb, var(--band-text) 25%, transparent)', '&:hover': { backgroundColor: 'color-mix(in srgb, var(--band-text) 18%, transparent)', borderColor: 'color-mix(in srgb, var(--band-text) 40%, transparent)' } } }}>
          <SecondaryButton
            onClick={() => navigate('/vendor/reservation-settings')}
            startIcon={<SettingsIcon sx={{ fontSize: 18 }} />}
            sx={{ fontSize: '0.813rem', px: 1.5 }}
          >
            Settings
          </SecondaryButton>
        </Box>}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats Grid */}
      {dailyStats && (
        <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-4 sm:mb-6">
          <StatsCard
            title="Today"
            value={dailyStats.total}
            subtitle={`${dailyStats.upcoming} upcoming`}
            icon={<CalendarIcon />}
            color={'var(--status-warn-text)'}
            bgColor={'var(--status-warn-bg)'}
          />
          <StatsCard
            title="Confirmed"
            value={dailyStats.confirmed}
            icon={<ConfirmedIcon />}
            color={'var(--status-ok-text)'}
            bgColor={'var(--status-ok-bg)'}
          />
          <StatsCard
            title="Seated"
            value={dailyStats.seated}
            icon={<SeatIcon />}
            color={'var(--status-info-text)'}
            bgColor={'var(--status-info-bg)'}
          />
          <StatsCard
            title="No Shows"
            value={dailyStats.noShow}
            icon={<WarningIcon />}
            color={'var(--status-bad-text)'}
            bgColor={'var(--status-bad-bg)'}
          />
        </div>
      )}

      {/* Feature Chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className={`feature-chip ${isReservationEnabled ? 'enabled' : 'disabled'}`}>
          <CalendarIcon sx={{ fontSize: 14 }} />
          Reservations {isReservationEnabled ? 'On' : 'Off'}
        </span>
        <span className={`feature-chip ${isWaitlistEnabled ? 'enabled' : 'disabled'}`}>
          <PeopleIcon sx={{ fontSize: 14 }} />
          Waitlist {isWaitlistEnabled ? 'On' : 'Off'}
        </span>
        <span className={`feature-chip ${isTableMergeEnabled ? 'enabled' : 'disabled'}`}>
          <TableIcon sx={{ fontSize: 14 }} />
          Table Merge {isTableMergeEnabled ? 'On' : 'Off'}
        </span>
      </div>

      {/* Search Bar */}
      <MuiTextField
        placeholder="Search by name, phone, or code..."
        size="small"
        fullWidth
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'var(--bg-surface)',
            borderRadius: 2,
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            '& fieldset': { borderColor: theme.gray[200] || '#E2E8F0' },
            '&:hover fieldset': { borderColor: theme.gray[400] || '#94A3B8' },
            '&.Mui-focused fieldset': { borderColor: theme.primary || '#806464' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: theme.gray[400] || '#94A3B8', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: searchQuery ? (
            <InputAdornment position="end">
              <AppIconButton
                icon={<CloseIcon sx={{ fontSize: 16 }} />}
                tooltip="Clear search"
                variant="ghost"
                size="small"
                onClick={() => setSearchQuery('')}
              />
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-4 p-1 rounded-lg overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch', backgroundColor: theme.gray[100] || '#F1F5F9' }}>
        <button
          className={`tab-button ${activeTab === 0 ? 'active' : ''}`}
          onClick={() => setActiveTab(0)}
        >
          <CalendarIcon sx={{ fontSize: 18 }} />
          Calendar
        </button>
        {isReservationEnabled && (
          <button
            className={`tab-button ${activeTab === 3 ? 'active' : ''}`}
            onClick={() => setActiveTab(3)}
          >
            <TableIcon sx={{ fontSize: 18 }} />
            Timeline
          </button>
        )}
        {isWaitlistEnabled && (
          <button
            className={`tab-button ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => setActiveTab(1)}
          >
            <PeopleIcon sx={{ fontSize: 18 }} />
            Waitlist
          </button>
        )}
        {isTableMergeEnabled && (
          <button
            className={`tab-button ${activeTab === 2 ? 'active' : ''}`}
            onClick={() => setActiveTab(2)}
          >
            <TableIcon sx={{ fontSize: 18 }} />
            Table Groups
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 0 && isReservationEnabled && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Calendar */}
            <div className={isWaitlistEnabled ? 'lg:col-span-3' : 'lg:col-span-4'}>
              <ReservationCalendar
                onCreateReservation={handleCreateReservation}
                onViewReservation={handleViewReservation}
                searchQuery={searchQuery}
              />
            </div>

            {/* Sidebar with Waitlist */}
            {isWaitlistEnabled && (
              <div className="lg:col-span-1">
                <WaitlistPanel
                  className="max-h-[calc(100dvh-280px)] sm:h-[500px]"
                  onSelectTable={handleWaitlistTableSelect}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 0 && !isReservationEnabled && (
          <div className="content-card p-6">
            <Alert severity="info">
              Reservation calendar is disabled. Enable reservations in settings.
            </Alert>
          </div>
        )}

        {activeTab === 3 && isReservationEnabled && (
          <ReservationTimeline
            reservations={reservations}
            tables={availableTables}
            settings={settings}
            selectedDate={selectedDate}
            onViewReservation={handleViewReservation}
          />
        )}

        {activeTab === 1 && isWaitlistEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <WaitlistPanel
              className="max-h-[calc(100dvh-280px)] sm:h-[600px]"
              onSelectTable={handleWaitlistTableSelect}
            />
            <div className="content-card p-6">
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: theme.text || '#1E293B' }}>
                Waitlist Statistics
              </Typography>
              {waitlistStats && (
                <div className="grid grid-cols-2 gap-4">
                  <StatsCard
                    title="Active"
                    value={waitlistStats.totalActive}
                    icon={<PeopleIcon />}
                    color={'var(--status-info-text)'}
                    bgColor={'var(--status-info-bg)'}
                  />
                  <StatsCard
                    title="Waiting"
                    value={waitlistStats.waiting}
                    icon={<PeopleIcon />}
                    color={'var(--status-warn-text)'}
                    bgColor={'var(--status-warn-bg)'}
                  />
                  <StatsCard
                    title="Notified"
                    value={waitlistStats.notified}
                    icon={<PeopleIcon />}
                    color="#8B5CF6"
                    bgColor="#EDE9FE"
                  />
                  <StatsCard
                    title="Avg Wait"
                    value={
                      waitlistStats.averageWaitMinutes > 0
                        ? `${waitlistStats.averageWaitMinutes}min`
                        : '--'
                    }
                    icon={<PeopleIcon />}
                    color="#06B6D4"
                    bgColor="#CFFAFE"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 2 && isTableMergeEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TableMergePanel
              availableTables={availableTables}
              className="max-h-[calc(100dvh-280px)] sm:h-[500px]"
              waitlistEntry={waitlistTableSelectEntry?.entry}
              waitlistAction={waitlistTableSelectEntry?.action}
              onWaitlistTableSelected={handleWaitlistTableSelected}
              onWaitlistCancel={handleWaitlistCancel}
            />
            <div className="content-card p-6">
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: theme.text || '#1E293B' }}>
                Table Merge Information
              </Typography>
              <Typography variant="body2" sx={{ color: theme.gray[500] || '#64748B', mb: 4 }}>
                Merge tables for larger parties. Merged tables share a single
                bill and can be managed together.
              </Typography>
              <div className="space-y-3">
                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: 'var(--accent-alt-bg)' }}>
                    <TableIcon sx={{ color: 'var(--accent-alt)' }} />
                  </div>
                  <div>
                    <Typography variant="body2" sx={{ color: theme.gray[500] || '#64748B' }}>
                      Active Merges
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.text || '#1E293B' }}>
                      {tableGroups.filter((g) => g.status === 'PENDING' || g.status === 'ACTIVE').length}
                    </Typography>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: 'var(--status-info-bg)' }}>
                    <TableIcon sx={{ color: 'var(--status-info-text)' }} />
                  </div>
                  <div>
                    <Typography variant="body2" sx={{ color: theme.gray[500] || '#64748B' }}>
                      Available Tables
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.text || '#1E293B' }}>
                      {availableTables.length}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reservation Detail Modal */}
      <ReservationDetailModal
        isOpen={detailModalOpen}
        onClose={() => { setDetailModalOpen(false); setViewingReservation(null); }}
        reservation={viewingReservation}
        onEdit={(r) => { setDetailModalOpen(false); setViewingReservation(null); handleEditReservation(r); }}
      />

      {/* Reservation Create/Edit Modal */}
      <ReservationModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        reservation={editingReservation || undefined}
        preselectedDate={preselectedDate}
      />
    </div>
  );
};

// ============================================================================
// MAIN PAGE (WITH PROVIDER)
// ============================================================================

const ReservationDashboard: React.FC = () => {
  const { user, isVendor, isSteward } = useAuth();

  if (!user || (!isVendor && !isSteward)) {
    return (
      <Box className="p-6">
        <Alert severity="error">
          You don't have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <ReservationProvider>
      <DashboardContent />
    </ReservationProvider>
  );
};

export default ReservationDashboard;
